import { ref, watch, nextTick } from 'vue';
import { useDisplayLogic } from '@rebuild/materials/shared/page_container/useDisplayLogic';

/**
 * 判断菜单是否是概览菜单
 * @param {Object} menu - 菜单对象
 * @returns {Boolean}
 */
export const isOverviewMenu = menu => menu.displayMode === 0;

/**
 * 检查菜单是否有子菜单
 * @param {Object} menu - 菜单对象
 * @returns {Boolean}
 */
export function checkChildrenMenus(menu) {
	const { pageMenuVOS = [] } = menu || {};
	return Array.isArray(pageMenuVOS) && pageMenuVOS.length > 0;
}

/**
 * 检查菜单是否有模块
 * @param {Object} menu - 菜单对象
 * @returns {Boolean}
 */
export function checkChildrenModules(menu) {
	const { menuType, moduleVOS = [] } = menu || {};

	const isModuleMenu = menuType === 1; // 组件型菜单
	if (isModuleMenu) return false;

	return Array.isArray(moduleVOS) && moduleVOS.length > 0;
}

/**
 * 判断模块是否选中 (升级版)
 * @param {Object} module - 当前渲染循环中的模块对象
 * @param {Object} displayModule - 当前激活的模块数据
 * @param {Object} context - 上下文对象
 * @param {String} context.parentMenuId - 当前渲染模块所属的菜单ID
 * @param {String} context.currentActiveMenuId - 当前激活的菜单ID
 * @returns {Boolean}
 */
export function checkModuleActive(module, displayModule, { parentMenuId, currentActiveMenuId }) {
	if (!displayModule) return false;

	// 1. 基础 ID 匹配
	const isIdMatch = displayModule?.moduleId === module?.moduleId;
	if (!isIdMatch) return false;

	// 2. 限定菜单范围
	// 只有当“当前渲染模块的父菜单”等于“当前激活的菜单”时，才算真正激活
	// 这样就区分了 MenuA 下的 ModuleX 和 MenuB 下的 ModuleX
	return parentMenuId === currentActiveMenuId;
}

/**
 * 判断菜单是否选中
 * @param {Object} menu - 菜单对象
 * @param {Object} displayMenu - 当前激活的菜单对象
 * @param {Object} displayModule - 当前激活的模块对象
 * @returns {Boolean}
 */
export function checkMenuActive(menu, displayMenu, displayModule) {
	if (!menu || !displayMenu) return false;

	const overviewMenu = isOverviewMenu(menu);

	// 如果当前菜单有模块被选中，菜单项不显示 active
	if (!overviewMenu && checkChildrenModules(menu) && menu.moduleVOS) {
		const hasActiveModule = menu.moduleVOS.some(moduleItem =>
			checkModuleActive(moduleItem, displayModule, {
				parentMenuId: menu.uuid,
				currentActiveMenuId: displayMenu.uuid
			})
		);
		if (hasActiveModule) return false;
	}

	// 匹配菜单选中
	return displayMenu.uuid === menu.uuid;
}

/**
 * 解析默认菜单
 * @param {Object} menu - 菜单对象
 * @returns {Object}
 */
export function resolveDefaultMenu(menu) {
	if (!menu) return null;

	const { pageMenuVOS = [] } = menu;
	const hasSubMenus = pageMenuVOS.length > 0;
	if (hasSubMenus) {
		const [firstMenu] = pageMenuVOS; // 优先匹配第一个子菜单
		return firstMenu;
	}

	return menu;
}

export function useNavMenu() {
	const { displayMenu, checkIsLinkMenu } = useDisplayLogic();
	const defaultActive = ref(displayMenu.value?.uuid);

	watch(
		() => displayMenu.value,
		val => {
			defaultActive.value = val?.uuid;
		}
	);

	const handleSelect = index => {
		const isLinkMenu = checkIsLinkMenu(index);
		if (isLinkMenu) {
			// 强制校准，避免外链菜单展示了选中状态
			const prevActiveIndex = defaultActive.value;
			defaultActive.value = null;

			nextTick(() => {
				defaultActive.value = prevActiveIndex;
			});
		}
	};

	return {
		defaultActive,
		handleSelect
	};
}
